import { t } from './i18n';
// ── SRS-алгоритм (FSRS-4.5) ───────────────────────────────────────────────────
//
// Реализация Free Spaced Repetition Scheduler v4.5 (Jarrett Ye, MIT License).
// Совместима с Anki 23.10+ "Modify Schedule".
//
// Состояния карточки (сохранены для обратной совместимости с UI):
//   "new"     → показывается впервые
//   "learn1"  → шаг заучивания, не последний (номер шага — в srs_interval)
//   "learn2"  → последний шаг заучивания / переучивания
//   Шаги задаются в настройках (по умолчанию 1m 10m и 10m — как в Anki).
//   "review"  → выпускная, интервал считается через FSRS
//
// Поля карточки:
//   srs_state       — состояние
//   srs_stability   — стабильность памяти S (дни), используется FSRS
//   srs_difficulty  — сложность D (1-10), используется FSRS
//   srs_reps        — общее число успешных повторов
//   srs_lapses      — число «забываний» в review-фазе
//   srs_last_review — ISO-дата последнего ревью
//   srs_interval    — копия последнего вычисленного интервала (для UI и legacy)
//   srs_ease        — legacy-поле, держим для миграции и совместимости
//   srs_due         — ISO-дата следующего показа
//
// Оценки — как в Anki (FSRS grades):
//   1 = Again «Снова»  2 = Hard «Трудно»  3 = Good «Хорошо»  4 = Easy «Легко»

// ── FSRS-4.5 веса (стандартные, из open-spaced-repetition) ────────────────────
const W = [
  0.40255, 1.18385, 3.173, 15.69105, 7.1949,
  0.5345, 1.4604, 0.0046, 1.54575, 0.1192,
  1.01925, 1.9395, 0.11, 0.29605, 2.2698,
  0.2315, 2.9898, 0.51655, 0.6621,
];

// FSRS-4.5 retrievability формула: R(t,S) = (1 + F·t/S)^C
const FACTOR = 19 / 81;     // ≈ 0.2346
const DECAY  = -0.5;

// Дефолты глобальных настроек (синкаются через settings.js)
export const FSRS_DEFAULTS = Object.freeze({
  retention:   0.90,   // целевое удержание
  dayStart:    4,      // час начала "нового дня" (0..23)
  newLimit:    10,     // дневной лимит новых карт (10 — чтобы новые не копились лавиной)
  reviewLimit: 200,    // дневной лимит повторений
  maxInterval: 36500,  // максимальный интервал, дней (как в Anki; 36500 = без ограничения)
  learnSteps:   Object.freeze([1, 10]), // шаги заучивания новой карты, минуты (Anki: 1m 10m)
  relearnSteps: Object.freeze([10]),    // шаги после «Снова» в повторении, минуты (Anki: 10m)
});

// ── Daily stats (Anki-style limits) ──────────────────────────────────────────
// Хранит сколько новых/повторений сделано за каждый «день» (с учётом dayStart).
// Структура: { "YYYY-MM-DD": { newDone: N, reviewDone: N } }
const DAILY_KEY = 'ac_fsrs_daily';

function _dayKey(dayStart) {
  const h = Number.isFinite(dayStart) ? dayStart : FSRS_DEFAULTS.dayStart;
  const shifted = new Date(Date.now() - h * 3600 * 1000);
  // toISOString даёт UTC; для day-boundary это OK (мы сдвинули вручную)
  return shifted.toISOString().slice(0, 10);
}

function _readDaily() {
  try { return JSON.parse(localStorage.getItem(DAILY_KEY) || '{}'); }
  catch { return {}; }
}

/** Получить статистику за сегодня. dayStart влияет на границу дня. */
export function getDailyStats(dayStart) {
  const data = _readDaily();
  return data[_dayKey(dayStart)] || { newDone: 0, reviewDone: 0 };
}

/**
 * Записать одно ревью. wasNew=true если карта была в состоянии "new"
 * ДО оценки. Возвращает обновлённую статистику дня (для UI-обновления).
 * Чистит старые записи >60 дней чтобы не пухнуть.
 */
export function recordDailyReview(wasNew, dayStart) {
  try {
    const data = _readDaily();
    const key = _dayKey(dayStart);
    if (!data[key]) data[key] = { newDone: 0, reviewDone: 0 };
    if (wasNew) data[key].newDone++;
    else        data[key].reviewDone++;

    // Cleanup: оставляем последние 60 дней
    const cutoff = new Date(Date.now() - 60 * 86400000).toISOString().slice(0, 10);
    for (const k of Object.keys(data)) if (k < cutoff) delete data[k];

    localStorage.setItem(DAILY_KEY, JSON.stringify(data));
    return data[key];
  } catch {
    return { newDone: 0, reviewDone: 0 };
  }
}

// ── Чтение настроек из localStorage (с fallback) ──────────────────────────────
function _num(key, def, min, max) {
  try {
    const v = parseFloat(localStorage.getItem(key));
    if (!Number.isFinite(v)) return def;
    return Math.max(min, Math.min(max, v));
  } catch { return def; }
}
function _int(key, def, min, max) {
  try {
    const v = parseInt(localStorage.getItem(key), 10);
    if (!Number.isFinite(v)) return def;
    return Math.max(min, Math.min(max, v));
  } catch { return def; }
}

// ── Шаги заучивания (как «Learning steps» / «Relearning steps» в Anki) ───────
// Строка «1m 10m», «10m 1h 1d»; число без буквы — минуты, русские «м ч д» тоже
// понимаются. Шаги — в минутах. Шагов заучивания минимум один; шагов
// переучивания может не быть (пустая строка) — тогда сразу новый интервал.
export function parseSteps(str) {
  const out = [];
  for (const tok of String(str || '').trim().split(/[\s,;]+/)) {
    const m = /^(\d+(?:[.,]\d+)?)\s*(s|m|h|d|с|м|ч|д)?$/i.exec(tok);
    if (!m) continue;
    const n = parseFloat(m[1].replace(',', '.'));
    const u = (m[2] || 'm').toLowerCase();
    const mins = n * ({ s: 1 / 60, 'с': 1 / 60, h: 60, 'ч': 60, d: 1440, 'д': 1440 }[u] || 1);
    if (mins > 0 && mins <= 1440 * 30) out.push(Math.round(mins * 100) / 100);
  }
  return out.slice(0, 10);
}
export function formatSteps(steps) {
  return (steps || []).map((m) => (m >= 1440 && m % 1440 === 0 ? `${m / 1440}d` : m >= 60 && m % 60 === 0 ? `${m / 60}h` : m < 1 ? `${Math.round(m * 60)}s` : `${m}m`)).join(' ');
}
function _steps(key, def, allowEmpty) {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return def;
    const s = parseSteps(raw);
    return s.length || allowEmpty ? s : def;
  } catch { return def; }
}

export function getFsrsSettings() {
  return {
    retention:   _num('ac_fsrs_retention', FSRS_DEFAULTS.retention,   0.70, 0.97),
    dayStart:    _int('ac_day_start_hour', FSRS_DEFAULTS.dayStart,    0,    23),
    newLimit:    _int('ac_new_limit',      FSRS_DEFAULTS.newLimit,    0,    9999),
    reviewLimit: _int('ac_review_limit',   FSRS_DEFAULTS.reviewLimit, 0,    9999),
    maxInterval: _int('ac_max_interval',   FSRS_DEFAULTS.maxInterval, 1,    36500),
    learnSteps:  _steps('ac_learn_steps',   FSRS_DEFAULTS.learnSteps,   false),
    relearnSteps:_steps('ac_relearn_steps', FSRS_DEFAULTS.relearnSteps, true),
  };
}

// ── Чистая математика FSRS ────────────────────────────────────────────────────
const clamp = (x, lo, hi) => Math.max(lo, Math.min(hi, x));

// Начальная стабильность по первой оценке (g=1..4)
const initStability = (g) => Math.max(0.1, W[g - 1]);

// FSRS-4.5: D₀(g) = w[4] - exp(w[5]·(g-1)) + 1  (эталон — ts-fsrs v4.5)
const initDifficulty = (g) => clamp(W[4] - Math.exp(W[5] * (g - 1)) + 1, 1, 10);

// Обновление сложности (FSRS-4.5): delta → linear damping → mean reversion к D₀(Easy=4)
function nextDifficulty(D, g) {
  const deltaD = -W[6] * (g - 3);
  // Linear damping: чем выше D, тем меньше изменение (защита от ease-hell)
  const Dp = D + deltaD * ((10 - D) / 9);
  // Mean-reversion к D₀(4) (Easy) — фикс относительно FSRS-4
  const Dpp = W[7] * initDifficulty(4) + (1 - W[7]) * Dp;
  return clamp(Dpp, 1, 10);
}

// Retrievability через t дней при стабильности S
const retrievability = (t, S) => Math.pow(1 + FACTOR * t / Math.max(0.01, S), DECAY);

// Не дальше «максимального интервала» из настроек (по умолчанию 100 лет — без ограничения).
const capInterval = (days, settings) => Math.min(days, Math.max(1, Number(settings?.maxInterval) || 36500));

// Интервал в днях для желаемого удержания
export function intervalForRetention(S, retention = FSRS_DEFAULTS.retention) {
  const r = clamp(retention, 0.50, 0.99);
  const I = (S / FACTOR) * (Math.pow(r, 1 / DECAY) - 1);
  return Math.max(1, Math.round(I));
}

// Стабильность после успешного ревью (g=2,3)
function stabilityAfterSuccess(D, S, R, g) {
  const hardPenalty = g === 2 ? W[15] : 1;     // Hard
  const easyBonus   = g === 4 ? W[16] : 1;     // Easy
  const factor = Math.exp(W[8])
    * (11 - D)
    * Math.pow(S, -W[9])
    * (Math.exp((1 - R) * W[10]) - 1)
    * hardPenalty
    * easyBonus;
  return Math.max(0.1, S * (1 + factor));
}

// Стабильность после "забыл" (g=1) — lapse
function stabilityAfterLapse(D, S, R) {
  const sMin = W[11]
    * Math.pow(D, -W[12])
    * (Math.pow(S + 1, W[13]) - 1)
    * Math.exp((1 - R) * W[14]);
  return Math.max(0.1, sMin);
}

// ── Lazy-миграция legacy-карты (srs_interval/srs_ease → srs_stability/D) ─────
function migrateLegacy(card) {
  if (Number.isFinite(card.srs_stability) && Number.isFinite(card.srs_difficulty)) {
    return card;
  }
  const interval = Number(card.srs_interval) || 0;
  const ease     = Number(card.srs_ease)     || 2.5;
  // S = последний интервал в днях (минимум 0.5). D = грубое отображение ease → 1..7
  // ease 2.5 (лёгкая) → D 2.5; ease 1.3 (сложная) → D 7.5
  const S = Math.max(0.5, interval);
  const D = clamp(11 - ease * 3.5, 1, 10);
  return { ...card, srs_stability: S, srs_difficulty: D };
}

// ── Главные функции ──────────────────────────────────────────────────────────

/**
 * Возвращает обновлённую копию карты после оценки.
 * opts.retention переопределяет глобальную настройку (для прогноза кнопок).
 */
export function srsNextState(card, rating, opts = {}) {
  const settings  = { ...getFsrsSettings(), ...opts };
  const retention = settings.retention;
  const state     = card.srs_state || "new";
  const now       = Date.now();

  // «Легко» в обучении — сразу выпуск в повторение с большим интервалом (как в Anki).
  const graduate = (g) => {
    const S = initStability(g);
    const D = initDifficulty(g);
    const interval = capInterval(intervalForRetention(S, retention), settings);
    return {
      ...card,
      srs_state:      "review",
      srs_stability:  S,
      srs_difficulty: D,
      srs_reps:       (Number(card.srs_reps) || 0) + 1,
      srs_lapses:     Number(card.srs_lapses) || 0,
      srs_last_review:new Date(now).toISOString(),
      srs_interval:   interval,
      srs_ease:       2.5,
      srs_due:        new Date(now + interval * 86400 * 1000).toISOString(),
    };
  };

  // Конец переучивания (после «Снова» в повторении): стабильность уже пересчитана
  // при забывании — с ней и возвращаемся в повторение, а не начинаем с нуля.
  const graduateRelearn = (g) => {
    const S = Number(card.srs_stability) || initStability(3);
    const D = Number(card.srs_difficulty) || initDifficulty(3);
    const base = capInterval(intervalForRetention(S, retention), settings);
    const interval = g === 4 ? capInterval(base + 1, settings) : base;
    return {
      ...card,
      srs_state:      "review",
      srs_stability:  S,
      srs_difficulty: D,
      srs_reps:       (Number(card.srs_reps) || 0) + 1,
      srs_last_review:new Date(now).toISOString(),
      srs_interval:   interval,
      srs_ease:       card.srs_ease || 2.5,
      srs_due:        new Date(now + interval * 86400 * 1000).toISOString(),
    };
  };

  // ── Фаза обучения (new / learn1 / learn2): шаги из настроек, как в Anki ────
  // learn1 — не последний шаг (номер шага в srs_interval), learn2 — последний.
  // Новая карта стоит на шаге 0. «Снова» — на шаг 0, «Трудно» — повторить шаг
  // (на первом — среднее первых двух), «Хорошо» — следующий шаг или выпуск.
  // Переучивание (у карты уже есть стабильность) идёт по своим шагам.
  if (state === "new" || state === "learn1" || state === "learn2") {
    const relearning = state !== "new" && Number(card.srs_stability) > 0;
    const steps = (relearning ? settings.relearnSteps : settings.learnSteps) || [];
    const done = relearning ? graduateRelearn : graduate;
    if (rating === 4 || !steps.length) return done(rating === 4 ? 4 : 3);
    const last = steps.length - 1;
    const cur = state === "learn2" ? last : state === "learn1" ? clamp(Math.round(Number(card.srs_interval) || 0), 0, last) : 0;
    const toStep = (i, minutes) => ({
      ...card,
      srs_state:    i >= last ? "learn2" : "learn1",
      srs_interval: i,
      srs_ease:     card.srs_ease || 2.5,
      srs_due:      new Date(now + minutes * 60 * 1000).toISOString(),
    });
    if (rating === 1) return toStep(0, steps[0]);
    if (rating === 2) {
      const hard = cur > 0 ? steps[cur]
        : steps.length > 1 ? (steps[0] + steps[1]) / 2
        : Math.min(steps[0] * 1.5, steps[0] + 1440);
      return toStep(cur, hard);
    }
    if (cur < last) return toStep(cur + 1, steps[cur + 1]);
    return done(3);
  }

  // ── Review-фаза: полный FSRS-расчёт ────────────────────────────────────────
  const c = migrateLegacy(card);
  const S = c.srs_stability;
  const D = c.srs_difficulty;
  const lastReview = c.srs_last_review ? new Date(c.srs_last_review).getTime() : (now - 86400000);
  const elapsedDays = Math.max(0, (now - lastReview) / 86400000);
  const R = retrievability(elapsedDays, S);

  if (rating === 1) {
    // Lapse: считаем новую (упавшую) стабильность и идём по шагам переучивания
    const newS = stabilityAfterLapse(D, S, R);
    const newD = nextDifficulty(D, 1);
    const lapsed = { ...c, srs_stability: newS, srs_difficulty: newD, srs_lapses: (Number(c.srs_lapses) || 0) + 1, srs_ease: c.srs_ease || 2.5 };
    const steps = settings.relearnSteps || [];
    if (!steps.length) {
      // Шагов переучивания нет — сразу новый (короткий) интервал.
      const interval = capInterval(intervalForRetention(newS, retention), settings);
      return { ...lapsed, srs_state: "review", srs_last_review: new Date(now).toISOString(), srs_interval: interval, srs_due: new Date(now + interval * 86400 * 1000).toISOString() };
    }
    return {
      ...lapsed,
      srs_state:    steps.length > 1 ? "learn1" : "learn2",
      srs_interval: 0,
      srs_due:      new Date(now + steps[0] * 60 * 1000).toISOString(),
    };
  }

  // rating 2 (Hard), 3 (Good) или 4 (Easy)
  const newS = stabilityAfterSuccess(D, S, R, rating);
  const newD = nextDifficulty(D, rating);
  const interval = capInterval(intervalForRetention(newS, retention), settings);
  return {
    ...c,
    srs_state:      "review",
    srs_stability:  newS,
    srs_difficulty: newD,
    srs_reps:       (Number(c.srs_reps) || 0) + 1,
    srs_last_review:new Date(now).toISOString(),
    srs_interval:   interval,
    srs_ease:       c.srs_ease || 2.5,
    srs_due:        new Date(now + interval * 86400 * 1000).toISOString(),
  };
}

/** Алиас для удобства */
export const srsNext = (card, rating, opts) => srsNextState(card, rating, opts);

/**
 * Метка интервала для кнопки рейтинга (для прогноза над Again/Hard/Good).
 * opts.retention переопределяет глобальную настройку (для live-прогноза слайдера).
 */
export function srsNextLabel(card, rating, opts = {}) {
  const next = srsNextState(card, rating, opts);
  const ms = new Date(next.srs_due).getTime() - Date.now();
  return _fmtInterval(ms);
}

function _fmtInterval(ms) {
  if (!Number.isFinite(ms) || ms <= 0) return t("сейчас");
  const mins = Math.round(ms / 60000);
  if (mins < 60) return t("{0} мин",[mins]);
  const hours = Math.round(mins / 60);
  if (hours < 24) return t("{0}ч",[hours]);
  const days = Math.round(mins / 1440);
  if (days < 30) return t("{0}д",[days]);
  const months = Math.round(days / 30);
  if (months < 12) return t("{0}мес",[months]);
  const years = (days / 365).toFixed(1);
  return t("{0}г",[years]);
}

/**
 * true если карточка должна быть показана.
 * Учитывает "Next Day Starts At" — карта due до следующего day-start считается
 * сегодняшней (как в Anki).
 */
export function srsDue(card, dayStartHour) {
  if (!card.srs_due) return true;
  const h = (dayStartHour === undefined)
    ? _int('ac_day_start_hour', FSRS_DEFAULTS.dayStart, 0, 23)
    : dayStartHour;
  const due = new Date(card.srs_due).getTime();
  const now = new Date();
  const next = new Date(now);
  next.setHours(h, 0, 0, 0);
  if (next.getTime() <= now.getTime()) next.setDate(next.getDate() + 1);
  return due < next.getTime();
}

/** Человекочитаемая метка "когда следующий повтор" */
export function srsDueLabel(card) {
  if (!card.srs_due || (card.srs_state || "new") === "new") return t("Новая");
  const mins = Math.round((new Date(card.srs_due) - new Date()) / 60000);
  if (mins <= 0) return t("Сейчас");
  if (mins < 60) return t("Через {0}м",[mins]);
  const days = Math.round(mins / 1440);
  if (days <= 0) return t("Скоро");
  if (days === 1) return t("Завтра");
  return t("Через {0}д",[days]);
}

// ── Порядок занятия как в Anki ───────────────────────────────────────────────
// Раньше очередь перемешивалась случайно. Теперь:
//   1) карточки на изучении, которым уже пора (learn1/learn2) — по сроку;
//   2) повторения — по сроку, самые просроченные первыми;
//   3) новые — в порядке колод (проектов), внутри проекта — по месту в аудио;
//      новые равномерно вкраплены между повторениями («вперемешку», как в Anki
//      по умолчанию) или идут после / перед ними (настройка ac_new_mix);
//   4) карточки на изучении, срок которых наступит позже сегодня, — в конце.

export const NEW_MIX_OPTIONS = [
  ['mix',    t("Вперемешку с повторениями")],
  ['after',  t("После повторений")],
  ['before', t("Перед повторениями")],
];

export function getNewMix() {
  try {
    const v = localStorage.getItem('ac_new_mix');
    return NEW_MIX_OPTIONS.some(([id]) => id === v) ? v : 'mix';
  } catch { return 'mix'; }
}

// Равномерно распределить b по a: a=[r,r,r,r], b=[n] → r r n r r.
export function interleave(a, b) {
  const out = [];
  let ai = 0, bi = 0;
  while (ai < a.length || bi < b.length) {
    const takeB = bi < b.length && (ai >= a.length || (bi + 1) / (b.length + 1) <= (ai + 1) / (a.length + 1));
    out.push(takeB ? b[bi++] : a[ai++]);
  }
  return out;
}

/**
 * cards — все карточки выбранных колод; deckRank(card) — номер колоды карточки
 * в пользовательском порядке. Возвращает { queue, overflow }: overflow — то, что
 * не влезло в дневные лимиты (можно предложить «ещё»).
 */
export function buildStudyQueue(cards, { deckRank = () => 0, dayStart, newQuota = Infinity, reviewQuota = Infinity, newMix = 'mix', now = Date.now() } = {}) {
  const t = (c) => (c.srs_due ? new Date(c.srs_due).getTime() : 0);
  const state = (c) => c.srs_state || 'new';
  const due = cards.filter((c) => srsDue(c, dayStart));
  const learning = due.filter((c) => state(c) === 'learn1' || state(c) === 'learn2').sort((a, b) => t(a) - t(b));
  const learnNow = learning.filter((c) => t(c) <= now);
  const learnLater = learning.filter((c) => t(c) > now);
  const reviews = due.filter((c) => state(c) === 'review').sort((a, b) => t(a) - t(b));
  const fresh = due.filter((c) => state(c) === 'new')
    .sort((a, b) => deckRank(a) - deckRank(b) || (Number(a.time_sec) || 0) - (Number(b.time_sec) || 0));
  const newSlice = fresh.slice(0, Math.max(0, newQuota));
  const reviewSlice = reviews.slice(0, Math.max(0, reviewQuota));
  const main = newMix === 'after' ? [...reviewSlice, ...newSlice]
    : newMix === 'before' ? [...newSlice, ...reviewSlice]
    : interleave(reviewSlice, newSlice);
  return {
    queue: [...learnNow, ...main, ...learnLater],
    overflow: [...fresh.slice(newSlice.length), ...reviews.slice(reviewSlice.length)],
  };
}

// Сколько реально повторить сегодня: карточки на изучении + повторения (до лимита
// повторений) + новые (до лимита новых, за вычетом уже начатых сегодня). Новые сверх
// лимита — запас на следующие дни, а не долг: их не показываем в «на сегодня».
export function todayPlan(cards, settings = getFsrsSettings(), daily = getDailyStats(settings.dayStart)) {
  const learnA = [], reviewA = [], freshA = [];
  for (const c of cards) {
    if (!srsDue(c, settings.dayStart)) continue;
    const st = c.srs_state || 'new';
    if (st === 'new') freshA.push(c); else if (st === 'review') reviewA.push(c); else learnA.push(c);
  }
  const newQ = settings.newLimit > 0 ? Math.max(0, settings.newLimit - (daily.newDone || 0)) : Infinity;
  const revQ = settings.reviewLimit > 0 ? Math.max(0, settings.reviewLimit - (daily.reviewDone || 0)) : Infinity;
  const due = (c) => (c.srs_due ? new Date(c.srs_due).getTime() : 0);
  reviewA.sort((a, b) => due(a) - due(b));
  const revList = reviewA.slice(0, revQ), newList = freshA.slice(0, newQ);
  const ids = new Set([...learnA, ...revList, ...newList].map((c) => String(c.id)));
  return { total: ids.size, learn: learnA.length, review: revList.length, fresh: newList.length,
    waitingNew: freshA.length - newList.length, waitingReview: reviewA.length - revList.length, ids };
}

// Карточки, которые сегодня уже повторяли (каждая — один раз, сколько бы раз ни
// нажимали «Снова»). «Сделано сегодня» = из них те, что на сегодня уже пройдены.
const REVIEWED_KEY = 'ac_reviewed_today';
export function getReviewedToday(dayStart) {
  try {
    const d = JSON.parse(localStorage.getItem(REVIEWED_KEY) || 'null');
    if (d && d.day === _dayKey(dayStart) && Array.isArray(d.ids)) return new Set(d.ids.map(String));
  } catch { /* битая запись */ }
  return new Set();
}
export function markReviewedToday(id, dayStart) {
  const set = getReviewedToday(dayStart);
  set.add(String(id));
  try { localStorage.setItem(REVIEWED_KEY, JSON.stringify({ day: _dayKey(dayStart), ids: [...set] })); } catch { /* приватный режим */ }
  return set;
}
// Сводка дня по уникальным карточкам: done — пройденные сегодня, all = done + осталось.
export function dayProgress(cards, settings = getFsrsSettings(), daily = getDailyStats(settings.dayStart)) {
  const plan = todayPlan(cards, settings, daily);
  const mine = new Set(cards.map((c) => String(c.id)));
  let done = 0;
  for (const id of getReviewedToday(settings.dayStart)) if (mine.has(id) && !plan.ids.has(id)) done++;
  return { ...plan, repeat: plan.learn + plan.review, done, all: done + plan.total, newLimit: settings.newLimit };
}

// Карточка после оценки ещё на изучении и вернётся в течение часа — показать её
// снова в этом же занятии (как «Снова» в Anki), а не ждать следующего захода.
export function staysInSession(card, now = Date.now()) {
  const s = card.srs_state || 'new';
  if (s !== 'learn1' && s !== 'learn2') return false;
  const due = card.srs_due ? new Date(card.srs_due).getTime() : 0;
  return due - now <= 60 * 60 * 1000;
}

// ── Порядок колод (проектов) ─────────────────────────────────────────────────
// Список id проектов в порядке, заданном перетаскиванием. Проекты, которых нет
// в списке (новые), идут после — по алфавиту.
const DECK_ORDER_KEY = 'ac_deck_order';

export function getDeckOrder() {
  try {
    const v = JSON.parse(localStorage.getItem(DECK_ORDER_KEY) || '[]');
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : [];
  } catch { return []; }
}

export function setDeckOrder(ids) {
  try { localStorage.setItem(DECK_ORDER_KEY, JSON.stringify(ids)); } catch {}
}

export function sortByDeckOrder(projects, order = getDeckOrder()) {
  const pos = new Map(order.map((id, i) => [id, i]));
  return [...projects].sort((a, b) => {
    const pa = pos.has(a.id) ? pos.get(a.id) : Infinity;
    const pb = pos.has(b.id) ? pos.get(b.id) : Infinity;
    if (pa !== pb) return pa - pb;
    return (a.name || '').localeCompare(b.name || '');
  });
}
